// StackedOperation — render columnar (vertical) de operaciones aritméticas
// básicas (suma, resta, multiplicación, división) tal como se escriben en
// cuadernos de Primaria: un operando sobre otro, alineados a la derecha, con
// el operador a la izquierda del último operando y una línea horizontal antes
// del resultado.
//
// Props:
//   operands       : number[] — al menos 2
//   operator       : "+" | "-" | "×" | "÷"
//   value          : string (respuesta del alumno) — opcional
//   onChange       : (v: string) => void — opcional (modo solo lectura si no se pasa)
//   readOnly       : bool (opcional, fuerza lectura)
//   showSolution   : bool — muestra el resultado en lugar del input
//   solution       : string|number — valor a mostrar cuando showSolution=true
//   size           : "sm" | "md" | "lg"  (default "md")
//   testId         : opcional
import { useMemo } from "react";

const OPERATOR_LABELS = { "+": "+", "-": "−", "×": "×", "÷": "÷" };

const SIZES = {
  sm: { cell: "w-7 h-9 text-lg",   row: "gap-1",   line: "h-[2px]" },
  md: { cell: "w-10 h-12 text-3xl", row: "gap-1.5", line: "h-[3px]" },
  lg: { cell: "w-14 h-16 text-5xl", row: "gap-2",   line: "h-[4px]" },
};

export default function StackedOperation({
  operands = [],
  operator = "+",
  value = "",
  onChange,
  readOnly = false,
  showSolution = false,
  solution,
  size = "md",
  testId,
}) {
  const sz = SIZES[size] || SIZES.md;

  // Calcula el ancho (#de columnas) en función del operando más largo y del resultado esperado.
  const cols = useMemo(() => {
    const lens = operands.map((n) => String(Math.abs(Number(n) || 0)).length);
    const maxOperand = Math.max(1, ...lens);
    const solStr = String(solution ?? value ?? "");
    return Math.max(maxOperand + 1, solStr.length || maxOperand);
  }, [operands, value, solution]);

  const isInteractive = !readOnly && !showSolution && typeof onChange === "function";

  // Render columnas con padding a la izquierda; el operador ocupa la columna 0 del último operando.
  const padDigits = (n) => {
    const s = String(Math.abs(Number(n) || 0));
    return Array(Math.max(0, cols - s.length)).fill("").concat(s.split(""));
  };

  return (
    <div className="inline-block bg-white rounded-2xl border-2 border-[#E1E5F0] p-5 select-none" data-testid={testId}>
      <div className="flex flex-col items-end font-mono font-extrabold text-[#1F2547] leading-none">
        {operands.map((op, i) => {
          const digits = padDigits(op);
          const isLast = i === operands.length - 1;
          return (
            <div key={i} className={`flex items-center ${sz.row}`}>
              {/* operador a la izquierda solo del último operando */}
              <div className={`${sz.cell} flex items-center justify-center ${isLast ? "text-[#A43790]" : "text-transparent"}`}>
                {isLast ? OPERATOR_LABELS[operator] || "+" : "·"}
              </div>
              {digits.map((d, j) => (
                <div key={j} className={`${sz.cell} flex items-center justify-center`}>
                  {d}
                </div>
              ))}
            </div>
          );
        })}

        {/* línea de resultado */}
        <div className={`flex ${sz.row} mt-1`}>
          <div className={`${sz.cell}`} />
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} className={`${sz.cell.split(" ")[0]} ${sz.line} bg-[#1F2547] rounded`} />
          ))}
        </div>

        {/* fila respuesta */}
        <div className={`flex items-center ${sz.row} mt-1`}>
          <div className={`${sz.cell}`} />
          {showSolution ? (
            padDigits(solution || 0).map((d, j) => (
              <div key={j} className={`${sz.cell} flex items-center justify-center text-[#3FA84D]`}>
                {d}
              </div>
            ))
          ) : isInteractive ? (
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={value || ""}
              onChange={(e) => {
                // sólo dígitos, máximo `cols + 1` (por carry); mantener limpio
                const v = e.target.value.replace(/[^0-9-]/g, "").slice(0, cols + 1);
                onChange(v);
              }}
              data-testid={testId ? `${testId}-input` : undefined}
              placeholder="?"
              className={`px-3 py-2 rounded-lg border-2 border-[#A43790] text-3xl font-extrabold font-mono text-[#A43790] text-right outline-none focus:bg-[#FFF1F8]`}
              style={{ width: `${cols * 2.6}rem` }}
            />
          ) : (
            // placeholder vacío en modo lectura sin solución
            Array.from({ length: cols }).map((_, j) => (
              <div key={j} className={`${sz.cell} flex items-center justify-center text-[#E1E5F0]`}>?</div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Calcula la respuesta correcta para un set de operandos + operador.
// Devuelve string (o "" si la división no es exacta o algo es inválido).
export function computeStackedAnswer(operands, operator) {
  const nums = (operands || []).map((x) => Number(x)).filter((x) => Number.isFinite(x));
  if (nums.length < 2) return "";
  let r = nums[0];
  for (let i = 1; i < nums.length; i++) {
    if (operator === "+") r += nums[i];
    else if (operator === "-") r -= nums[i];
    else if (operator === "×") r *= nums[i];
    else if (operator === "÷") {
      if (nums[i] === 0) return "";
      r = r / nums[i];
    } else return "";
  }
  if (!Number.isFinite(r)) return "";
  // si división no entera, devolver con 2 decimales; en operaciones enteras, entero
  return Number.isInteger(r) ? String(r) : String(Math.round(r * 100) / 100);
}
